import React from 'react';
import { Drawer } from 'vaul';
import { cn } from '@shared/lib/utils';
import { useHistoryDismiss } from './useHistoryDismiss';

export interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  snapPoints?: (number | string)[];
  trigger?: React.ReactNode;
  className?: string;
  enableHistoryDismiss?: boolean;
}

export const BottomSheet: React.FC<BottomSheetProps> = ({
  open,
  onOpenChange,
  title,
  description,
  children,
  snapPoints,
  trigger,
  className,
  enableHistoryDismiss = true,
}) => {
  const handleDismiss = React.useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  useHistoryDismiss(
    enableHistoryDismiss && open,
    handleDismiss,
    'bottomsheet'
  );

  return (
    <Drawer.Root
      open={open}
      onOpenChange={onOpenChange}
      snapPoints={snapPoints}
      shouldScaleBackground={false}
    >
      {trigger && <Drawer.Trigger asChild>{trigger}</Drawer.Trigger>}
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm transition-opacity data-[state=closed]:hidden" />
        <Drawer.Content
          className={cn(
            'fixed inset-x-0 bottom-0 z-50 flex max-h-[92dvh] flex-col rounded-t-[20px] bg-background border-t border-border shadow-2xl focus:outline-none pb-[calc(var(--safe-bottom)+1rem)] data-[state=closed]:hidden',
            className
          )}
        >
          {/* Grab handle */}
          <div className="mx-auto mt-3 h-1.5 w-12 rounded-full bg-muted-foreground/30 shrink-0" />

          {title ? (
            <div className="px-4 pt-3 pb-2 border-b border-border/40 shrink-0">
              <Drawer.Title className="text-base font-semibold text-foreground tracking-tight">
                {title}
              </Drawer.Title>
              {description ? (
                <Drawer.Description className="text-xs text-muted-foreground mt-0.5">
                  {description}
                </Drawer.Description>
              ) : (
                <Drawer.Description className="sr-only">Sheet description</Drawer.Description>
              )}
            </div>
          ) : (
            <>
              <Drawer.Title className="sr-only">Sheet</Drawer.Title>
              <Drawer.Description className="sr-only">Sheet description</Drawer.Description>
            </>
          )}

          <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-3 min-h-0">
            {children}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

export default BottomSheet;
